import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Paper, Alert, CircularProgress,
  Fade, Divider, Chip, Stepper, Step, StepLabel, InputAdornment,
} from '@mui/material';
import { Search, Building2, Globe, Rocket, Check, ShieldCheck, Copy, ExternalLink } from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const SectionCard = styled(Paper)(({ theme }) => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const formatDomain = (input) => {
  return (input || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 30);
};

const formatCNPJ = (v) => {
  const n = (v || '').replace(/\D/g, '').substring(0, 14);
  if (n.length <= 2) return n;
  if (n.length <= 5) return `${n.slice(0, 2)}.${n.slice(2)}`;
  if (n.length <= 8) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5)}`;
  if (n.length <= 12) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8)}`;
  return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8, 12)}-${n.slice(12)}`;
};

const formatTelefoneInput = (v) => {
  const n = (v || '').replace(/\D/g, '').substring(0, 11);
  if (n.length <= 2) return n;
  if (n.length <= 6) return `(${n.slice(0, 2)}) ${n.slice(2)}`;
  if (n.length <= 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
  return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
};

export default function CriarVerificaix() {
  const navigate = useNavigate();

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();

  const [cnpjInput, setCnpjInput] = useState('');
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjData, setCnpjData] = useState(null);

  const [formData, setFormData] = useState({
    dominio: '',
    telefone: '',
    email: '',
    metaTag: '',
  });

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createdUrl, setCreatedUrl] = useState('');

  if (user.tipo !== 'admin') {
    return (
      <Fade in timeout={400}>
        <Box sx={{ p: 4 }}>
          <Alert severity="error">Esta função é restrita ao administrador.</Alert>
          <Button onClick={() => navigate('/dashboard')} sx={{ mt: 2 }} variant="contained">
            Voltar
          </Button>
        </Box>
      </Fade>
    );
  }

  const activeStep = !cnpjData ? 0 : createdUrl ? 2 : 1;

  const handleBuscarCNPJ = async () => {
    const cnpjLimpo = cnpjInput.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) {
      setError('CNPJ deve ter 14 dígitos');
      return;
    }
    setError('');
    setCnpjLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/sites/cnpj/${cnpjLimpo}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      );
      if (res.data.success) {
        const d = res.data.data;
        setCnpjData(d);
        const baseNome = d.nome_fantasia || d.razao_social || '';
        setFormData((prev) => ({
          ...prev,
          dominio: prev.dominio || formatDomain(baseNome),
          email: prev.email || (d.email || ''),
          telefone: prev.telefone || (d.telefone ? formatTelefoneInput(d.telefone) : ''),
        }));
      } else {
        setError(res.data.message || 'CNPJ não encontrado');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao buscar CNPJ. Tente novamente.');
    } finally {
      setCnpjLoading(false);
    }
  };

  const handleGerarSite = async () => {
    setError('');
    setSuccess('');

    if (!formData.dominio) { setError('Domínio é obrigatório.'); return; }
    if (!formData.telefone) { setError('Telefone é obrigatório.'); return; }
    if (!formData.email) { setError('E-mail é obrigatório.'); return; }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sites/generate-verificaix`,
        {
          cnpjData,
          telefone: formData.telefone,
          email: formData.email,
          metaTag: formData.metaTag,
          dominio: formData.dominio,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      if (res.data.success) {
        setCreatedUrl(res.data.url);
        setSuccess('Site gerado com sucesso!');
      } else {
        setError(res.data.message || 'Erro ao gerar site.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao gerar site. Tente novamente.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 980, mx: 'auto', p: { xs: 1, sm: 2 } }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <ShieldCheck size={28} color="#A78BFA" />
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
              Criar site Verificaix
            </Typography>
            <Chip label="Admin" size="small" sx={{ background: 'rgba(124,58,237,0.2)', color: '#A78BFA', fontWeight: 700 }} />
          </Box>
          <Typography sx={{ color: '#9CA3AF' }}>
            Geração de site no subdomínio <strong style={{ color: '#A78BFA' }}>*.verificaix.com</strong> com SSL ativo. Layout fixo — só os dados da empresa são preenchidos.
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          <Step><StepLabel>Buscar CNPJ</StepLabel></Step>
          <Step><StepLabel>Gerar site</StepLabel></Step>
          <Step><StepLabel>Concluído</StepLabel></Step>
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <SectionCard>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
            <Building2 size={20} color="#A78BFA" />
            <Typography variant="h6" sx={{ color: '#fff' }}>1. Dados da empresa (CNPJ)</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              label="CNPJ"
              value={cnpjInput}
              onChange={(e) => setCnpjInput(formatCNPJ(e.target.value))}
              placeholder="00.000.000/0000-00"
              sx={{ flex: 1, minWidth: 240 }}
              disabled={!!cnpjData}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color="#9CA3AF" />
                  </InputAdornment>
                ),
              }}
            />
            {!cnpjData ? (
              <Button
                variant="contained"
                onClick={handleBuscarCNPJ}
                disabled={cnpjLoading}
                sx={{ minHeight: 56, px: 3 }}
              >
                {cnpjLoading ? <CircularProgress size={18} /> : 'Consultar'}
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={() => { setCnpjData(null); setCreatedUrl(''); setSuccess(''); }}
                sx={{ minHeight: 56, px: 3 }}
              >
                Trocar CNPJ
              </Button>
            )}
          </Box>

          {cnpjData && (
            <Box sx={{ mt: 2.5, p: 2, borderRadius: 2, background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.2)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Check size={16} color="#10B981" />
                <Typography sx={{ color: '#fff', fontWeight: 600 }}>{cnpjData.razao_social}</Typography>
              </Box>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>
                CNPJ {formatCNPJ(String(cnpjData.cnpj || ''))} • {cnpjData.municipio}-{cnpjData.uf} • {cnpjData.bairro || '—'}
              </Typography>
            </Box>
          )}
        </SectionCard>

        {cnpjData && !createdUrl && (
          <SectionCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
              <Rocket size={20} color="#A78BFA" />
              <Typography variant="h6" sx={{ color: '#fff' }}>2. Gerar site</Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Telefone (com DDD)"
                placeholder="(11) 95637-2126"
                value={formData.telefone}
                onChange={(e) => setFormData({ ...formData, telefone: formatTelefoneInput(e.target.value) })}
              />
              <TextField
                label="E-mail"
                placeholder="contato@empresa.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <TextField
                label="Meta Tag (Facebook Domain Verification)"
                placeholder="vt7fwbhlazoilvl2m9ff8ex0mo4r2j"
                value={formData.metaTag}
                onChange={(e) => setFormData({ ...formData, metaTag: e.target.value.trim() })}
                helperText="Cole apenas o conteúdo (content) da meta tag."
              />
              <TextField
                label="Domínio (subdomínio)"
                placeholder="meusite"
                value={formData.dominio}
                onChange={(e) => setFormData({ ...formData, dominio: formatDomain(e.target.value) })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Globe size={16} color="#9CA3AF" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>.verificaix.com</Typography>
                    </InputAdornment>
                  ),
                }}
                helperText={formData.dominio ? `URL final: https://${formData.dominio}.verificaix.com` : 'Use letras minúsculas, números e hífens.'}
              />
            </Box>

            <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.06)' }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleGerarSite}
                disabled={creating}
                sx={{ minHeight: 50, px: 4, fontWeight: 700 }}
              >
                {creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Gerar site'}
              </Button>
            </Box>
          </SectionCard>
        )}

        {createdUrl && (
          <SectionCard>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{
                width: 64, height: 64, mx: 'auto', borderRadius: '50%',
                background: 'rgba(16,185,129,0.15)', display: 'grid', placeItems: 'center', mb: 2,
              }}>
                <Check size={32} color="#10B981" />
              </Box>
              <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>Site gerado com sucesso</Typography>
              <Typography sx={{ color: '#9CA3AF', mb: 3 }}>
                Está disponível agora no domínio abaixo (com SSL).
              </Typography>
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1,
                background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)',
                borderRadius: 2, px: 2, py: 1, mb: 3,
              }}>
                <Globe size={16} color="#A78BFA" />
                <Typography sx={{ color: '#fff', fontWeight: 700 }}>{createdUrl}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<Copy size={16} />}
                  onClick={() => navigator.clipboard.writeText(createdUrl)}
                >
                  Copiar URL
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ExternalLink size={16} />}
                  href={createdUrl}
                  target="_blank"
                  rel="noopener"
                >
                  Abrir site
                </Button>
                <Button onClick={() => navigate('/meus-sites')} variant="text">
                  Ir para Meus Sites
                </Button>
              </Box>
            </Box>
          </SectionCard>
        )}
      </Box>
    </Fade>
  );
}
